import { useEffect } from "react";
import { api, fileUrl } from "@/lib/api";

/**
 * Sets document meta tags (title, description, keywords, OG) from site settings.
 * Runs once on app mount.
 */
export default function useSiteSEO() {
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/settings?_=${Date.now()}`);
        if (!data) return;

        const setMeta = (attrName, attrValue, content) => {
          if (!content) return;
          let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attrName, attrValue);
            document.head.appendChild(el);
          }
          el.setAttribute("content", content);
        };

        if (data.seo_title) document.title = data.seo_title;
        setMeta("name", "description", data.seo_description);
        setMeta("name", "keywords", data.seo_keywords);
        setMeta("property", "og:title", data.seo_title || data.org_name_tr);
        setMeta("property", "og:description", data.seo_description);
        setMeta("property", "og:image", data.og_image ? fileUrl(data.og_image) : "");
        setMeta("property", "og:type", "website");
        setMeta("name", "twitter:card", data.og_image ? "summary_large_image" : "summary");
        setMeta("name", "twitter:site", data.twitter_handle);
        setMeta("name", "twitter:title", data.seo_title || data.org_name_tr);
        setMeta("name", "twitter:description", data.seo_description);
        setMeta("name", "twitter:image", data.og_image ? fileUrl(data.og_image) : "");
        if (data.google_verification) setMeta("name", "google-site-verification", data.google_verification);

        // Custom favicon from admin settings
        if (data.favicon) {
          const faviconUrl = fileUrl(data.favicon);
          let link = document.querySelector("link[rel='icon']");
          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }
          link.href = faviconUrl;
          // Also set apple-touch-icon
          let apple = document.querySelector("link[rel='apple-touch-icon']");
          if (!apple) {
            apple = document.createElement("link");
            apple.rel = "apple-touch-icon";
            document.head.appendChild(apple);
          }
          apple.href = faviconUrl;
        }

        // Google Analytics
        if (data.google_analytics_id && !window.__gaLoaded) {
          window.__gaLoaded = true;
          const s = document.createElement("script");
          s.async = true;
          s.src = `https://www.googletagmanager.com/gtag/js?id=${data.google_analytics_id}`;
          document.head.appendChild(s);
          const inline = document.createElement("script");
          inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${data.google_analytics_id}');`;
          document.head.appendChild(inline);
        }
      } catch (e) { /* silent */ }
    })();
  }, []);
}
